import CardOnFile from '@liveauctioneers/caterwaul-components/lib/CardOnFile/CardOnFile';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import InPageMessage from '@liveauctioneers/caterwaul-components/lib/InPageMessage/InPageMessage';
import Radio from '@liveauctioneers/caterwaul-components/lib/Radio/Radio';
import { CreditCard } from '@liveauctioneers/caterwaul-components/lib/types/CreditCard';
import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { H3 } from '@liveauctioneers/hammer-ui-core/text';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import BankAccountStatus from '@/enums/bankAccountStatus';
import useIsServerSideRendered from '@/hooks/useIsServerSideRendered';
import { useAppSelector } from '@/redux/hooks';
import { getAuthFailedCardIds } from '@/redux/modules/registerForCatalog';
import noop from '@/utils/noop';

import BankAccountOnFileWithPlaidVerifyContainer from '../Plaid/BankAccountOnFileWithPlaidVerifyContainer';

type Props = {
    backgroundColor?: string;
    backgroundColorSelected?: string;
    bankAccounts?: BankAccount[];
    borderColor?: string;
    creditCards?: CreditCard[];
    deleteBankAccount?: (bankAccountId: number) => void;
    deleteCard?: (cardId: number) => void;
    notSelectedACHLabel?: ReactNode;
    notSelectedCardLabel?: ReactNode;
    paymentError?: React.ReactNode;
    selectBankAccount?: (bankAccount: BankAccount) => void;
    selectCard?: (creditCard: CreditCard) => void;
    selectedACHLabel?: ReactNode;
    selectedCard?: CreditCard;
    selectedCardLabel?: ReactNode;
    title?: ReactNode;
    validationError?: boolean;
};

const PaymentMethodSelection = ({
    bankAccounts,
    creditCards,
    deleteBankAccount,
    deleteCard,
    notSelectedACHLabel = <FormattedMessage id="paymentMethodSelection.notSelectedACHLabel" />,
    notSelectedCardLabel = <FormattedMessage id="paymentMethodSelection.notSelectedCardLabel" />,
    paymentError,
    selectBankAccount = noop,
    selectCard = noop,
    selectedACHLabel = <FormattedMessage id="paymentMethodSelection.selectedACHLabel" />,
    selectedCard = null,
    selectedCardLabel = <FormattedMessage id="paymentMethodSelection.selectedCardLabel" />,
    title = null,
}: Props) => {
    const authFailedCardIds: number[] = useAppSelector((state) => getAuthFailedCardIds(state));
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>(
        selectedCard ? `card-${selectedCard?.id}` : ''
    );
    const prevSelectedCard = usePrevious(selectedCard);
    const prevCardsLength = usePrevious(creditCards?.length);
    const prevBankAccountsLength = usePrevious(bankAccounts?.length);
    const isSSR = useIsServerSideRendered();

    useEffect(() => {
        if (
            !selectedPaymentMethodId ||
            prevCardsLength !== creditCards?.length ||
            prevSelectedCard?.id !== selectedCard?.id ||
            prevBankAccountsLength !== bankAccounts?.length
        ) {
            if (creditCards) {
                const defaultCardOnFile = creditCards.find((cof) => cof.defaultCard);
                setSelectedPaymentMethodId(selectedCard ? `card-${selectedCard?.id}` : `card-${defaultCardOnFile?.id}`);
                //just to make sure the parent is on the same page.
                if (!selectedPaymentMethodId && Boolean(defaultCardOnFile?.id) && selectCard) {
                    selectCard(defaultCardOnFile);
                }
            } else if (bankAccounts) {
                const defaultBankAccount = bankAccounts.find((bankAccount) => bankAccount.defaultAccount);
                setSelectedPaymentMethodId(`bankAccount-${defaultBankAccount?.bankAccountId}`);
                //just to make sure the parent is on the same page.
                if (defaultBankAccount && selectBankAccount) {
                    selectBankAccount(defaultBankAccount);
                }
            }
        }
    }, [
        bankAccounts,
        creditCards,
        prevBankAccountsLength,
        prevCardsLength,
        selectBankAccount,
        selectCard,
        selectedPaymentMethodId,
        selectedCard,
        prevSelectedCard?.id,
    ]);

    const mapRadio = useCallback(
        (
            id: number,
            radioId: string,
            selected: boolean,
            disabled: boolean,
            label: ReactNode,
            child: ReactNode,
            onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
        ) => {
            return (
                <StyledRadio
                    disabled={disabled}
                    id={radioId}
                    key={id}
                    label={child}
                    onClick={onClick}
                    selectedName={selectedPaymentMethodId}
                />
            );
        },
        [selectedPaymentMethodId]
    );

    const creditCardRadios = useMemo(() => {
        if (!creditCards) {
            return null;
        }
        // const sortedcreditCards = creditCards.sort((cardA, cardB) => {
        //     //TODO add created to the type
        //     return new Date(cardB.created).getTime() - new Date(cardA.created).getTime();
        // });
        return creditCards.map((creditCard) => {
            const radioId = `card-${creditCard.id}`;
            const selected = selectedPaymentMethodId === radioId;
            const failedAuth = authFailedCardIds.includes(creditCard.id);
            const label = isSSR ? (
                <StyledCardOnFileLoader>
                    <Loading />
                </StyledCardOnFileLoader>
            ) : (
                <CardOnFile
                    creditCard={creditCard}
                    deleteCard={
                        deleteCard
                            ? (event) => {
                                  event.stopPropagation();
                                  if (selected) {
                                      setSelectedPaymentMethodId(undefined);
                                  }
                                  deleteCard(creditCard.id);
                              }
                            : undefined
                    }
                    deleteCardLinkText="Remove"
                    isDefault={creditCard.defaultCard}
                    isRadio
                    processingErr={failedAuth}
                />
            );
            return mapRadio(
                creditCard.id,
                radioId,
                selected,
                failedAuth,
                selected ? selectedCardLabel : notSelectedCardLabel,
                label,
                (event) => {
                    const id = event.currentTarget.id;
                    setSelectedPaymentMethodId(id);
                    selectCard(creditCard);
                }
            );
        });
    }, [
        creditCards,
        selectedPaymentMethodId,
        authFailedCardIds,
        isSSR,
        deleteCard,
        mapRadio,
        selectedCardLabel,
        notSelectedCardLabel,
        selectCard,
    ]);

    const bankAccountRadios = useMemo(() => {
        if (!bankAccounts) {
            return null;
        }
        return bankAccounts.map((bankAccount) => {
            const radioId = `bankAccount-${bankAccount.bankAccountId}`;
            const selected = selectedPaymentMethodId === radioId;
            return mapRadio(
                bankAccount.bankAccountId,
                radioId,
                selected,
                bankAccount.status === BankAccountStatus.Pending ||
                    bankAccount.status === BankAccountStatus.PendingManual ||
                    bankAccount.status === BankAccountStatus.PendingAutomatic,
                selected ? selectedACHLabel : notSelectedACHLabel,
                <BankAccountOnFileWithPlaidVerifyContainer
                    bankAccount={bankAccount}
                    deleteAccount={
                        deleteBankAccount
                            ? (event) => {
                                  event.stopPropagation();
                                  if (selected) {
                                      setSelectedPaymentMethodId(undefined);
                                  }
                                  deleteBankAccount(bankAccount.bankAccountId);
                              }
                            : undefined
                    }
                    deleteAccountLinkText="Remove"
                />,
                (event) => {
                    const id = event.currentTarget.id;
                    setSelectedPaymentMethodId(id);
                    selectBankAccount(bankAccount);
                }
            );
        });
    }, [
        bankAccounts,
        deleteBankAccount,
        mapRadio,
        notSelectedACHLabel,
        selectBankAccount,
        selectedACHLabel,
        selectedPaymentMethodId,
    ]);

    return (
        <>
            {title && (
                <H3
                    className="mt-[10px]"
                    color="secondary"
                >
                    {title}
                </H3>
            )}
            <div>
                {/* Not using Radios component here so we can fully control it ourselves. */}
                {creditCardRadios}
                {bankAccountRadios}
                {Boolean(paymentError) && <StyledInPageMessage type="error">{paymentError}</StyledInPageMessage>}
            </div>
        </>
    );
};

export default PaymentMethodSelection;

const StyledInPageMessage = styled(InPageMessage)`
    margin-top: 10px;
`;

const StyledRadio = styled(Radio).attrs(() => ({
    radioCircleClassName: 'radioCircleClassName',
}))`
    width: 100%;
    padding: 10px 0 0 25px;

    .radioCircleClassName {
        top: 36px;
        left: 0;
    }
`;

const StyledCardOnFileLoader = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    height: 98px;
    padding-bottom: 26px;
`;
